/*!
 * ullc-catalogue-fix.js — 2026-08-13
 *
 * Makes the frameless glass calculator quote only sizes James Glen makes, price
 * the hinge panel as a hinge panel, and draw the fence the customer is buying.
 *
 *   pool fence panel  100-2000 in 50mm steps, plus 2100   (there is no 2050)
 *   hinge panel        600-1800 in 100mm steps            (13 sizes only)
 *   raked panel        sized by its high edge; the width is always 1200
 *
 * Eight defects. Full write-up and the money table:
 * claude/click-fencing-CATALOGUE-SIZE-AUDIT-GLASS-2026-08-13.md
 *
 *  1. The hinge line was priced with _glassPanelUnitPrice() — the plain-panel
 *     lookup. An 1800mm hinge panel billed at $142.79 instead of $175.19.
 *     $7-$32 short on every glass quote containing a gate.
 *  2. The hinge panel is never sized by the solver; it is the residual after
 *     panels, gate and gaps, so it landed on the 50mm grid (1350, 1450, ...)
 *     or above 1800. 16% of gated sections quoted an unbuyable hinge panel.
 *  3. Pass G Stage 2 may grow glass past the selected max toward the 2100mm
 *     structural limit. Pete 2026-08-13: keep the step-up, clamp it to the
 *     ladder — 2000 and 2100 are fine, 2050 is not.
 *  4. generateSectionPanelLayout copied the regular panel width into the hinge
 *     panel, so the 3D view drew 4750mm of fence into a 4600mm run.
 *  5. _buildCartItemsForSection fell back to variants.find(v => v.available) —
 *     the 600mm hinge panel — when the width had no variant, without recording
 *     an unresolved component, so the checkout guard never fired.
 *  6. Wall-to-glass never set wallGateEnd, so the solver returned a
 *     glass-to-glass shape (with a hinge panel) while the renderer took the
 *     wall-to-glass branch (no hinge panel) and dropped it. A 4500mm run drew
 *     1850mm of glass, a gate and an 1833mm hole, and quoted one panel instead
 *     of two — about $142 of glass missing from every wall-to-glass quote.
 *  7. A raked panel beside a gate can leave a run that cannot be built at all:
 *     under ~2650mm the leftover hinge panel falls below the 600mm minimum, and
 *     at 4000-4050mm the solver opens the end gaps past the 80mm build limit
 *     (the renderer then spreads one of them past the 100mm pool-safety limit).
 *     Neither is repairable by resizing glass, so both are reported to the
 *     customer instead of quoted.
 *  8. Wall-to-glass carts carried a hinge panel that is not part of the fence,
 *     sized from the last regular panel — a 2000mm run ordered an 1150mm
 *     "hinge panel". Removed.
 *
 * Sizes snap DOWN (the governing fit-down rule). Freed millimetres go first to
 * a regular panel that can take a 50mm step and stay on the ladder, then to the
 * variable gaps, so gap spacing stays near target. The 7mm latch and 10mm hinge
 * gaps never flex. Section totals stay exact to the millimetre.
 *
 * Self-installing, idempotent.
 */
(function () {
  'use strict';

  var FLAG = '__cfCatalogueSizePatched';
  var MAX_TRIES = 200, tries = 0;

  var PANEL_LADDER = [];
  for (var a = 100; a <= 2000; a += 50) PANEL_LADDER.push(a);
  PANEL_LADDER.push(2100);

  var HINGE_LADDER = [];
  for (var b = 600; b <= 1800; b += 100) HINGE_LADDER.push(b);

  var MIN_GAP = 10, LATCH_GAP = 7, HINGE_GAP = 10, MAX_GAP = 80;

  function sum(x) { var t = 0; for (var i = 0; i < x.length; i++) t += x[i]; return t; }
  function onLadder(v, l) { return l.indexOf(v) !== -1; }
  function snapDown(v, l) { var best = l[0]; for (var i = 0; i < l.length; i++) if (l[i] <= v) best = l[i]; return best; }

  // Gate hardware gaps must never flex. Prefer the solver's own fixedGapIndices;
  // where absent, a 7mm gap can only be the latch (no variable gap may sit below
  // MIN_GAP=10) and the 10mm hinge gap is its neighbour.
  function variableGapIndices(calc, gaps) {
    var fixed = {}, d = calc.fixedGapIndices, i;
    if (d && d.length) {
      for (i = 0; i < d.length; i++) fixed[d[i]] = true;
    } else if (calc.gatePanels) {
      for (i = 0; i < gaps.length; i++) {
        if (gaps[i] === LATCH_GAP) {
          fixed[i] = true;
          if (gaps[i - 1] === HINGE_GAP) fixed[i - 1] = true;
          else if (gaps[i + 1] === HINGE_GAP) fixed[i + 1] = true;
        }
      }
    }
    var out = [];
    for (i = 0; i < gaps.length; i++) if (!fixed[i]) out.push(i);
    return out;
  }

  function snapCalculation(calc, section, self) {
    if (!calc || !Array.isArray(calc.panelWidths) || !Array.isArray(calc.gapSpacings)) return calc;
    var L = Number(calc.originalSectionLength || (section && section.length) || 0);
    if (!L) return calc;

    var panels = calc.panelWidths.slice(), gaps = calc.gapSpacings.slice();
    var gateW = calc.gatePanels ? Number((section && section.gateLength) || 0) : 0;

    // Raked panels are sized by their high edge, not their width — the width is
    // always 1200 — so they are never snapped, only counted toward the run.
    var rakedW = 0;
    if (calc.rakedPanels && self && typeof self.getRakedPanelWidth === 'function') {
      rakedW = Number(self.getRakedPanelWidth()) * calc.rakedPanels || 0;
    }

    var hasHinge = !!calc.hingePanels;
    var hinge = 0, i;

    if (hasHinge) {
      hinge = calc.hingeWidth > 0 ? Number(calc.hingeWidth)
                                  : (L - sum(panels) - gateW - rakedW - sum(gaps));
      if (!(hinge > 0)) return calc;
      // Only act when the pieces we know about actually add up to the run.
      if (sum(panels) + gateW + rakedW + hinge + sum(gaps) !== L) return calc;
    } else if (sum(panels) + gateW + rakedW + sum(gaps) !== L) {
      return calc;
    }

    var freed = 0;

    if (hasHinge && !onLadder(hinge, HINGE_LADDER)) {
      var nh = snapDown(hinge, HINGE_LADDER);
      if (nh > hinge) return calc;           // under 600mm: nothing to snap down to
      freed += hinge - nh;
      hinge = nh;
    }

    for (i = 0; i < panels.length; i++) {
      if (!onLadder(panels[i], PANEL_LADDER)) {
        var np = snapDown(panels[i], PANEL_LADDER);
        if (np > panels[i]) continue;        // under 100mm: leave it to the solver
        freed += panels[i] - np;
        panels[i] = np;
      }
    }

    if (freed === 0) {
      if (hasHinge) calc.hingeWidth = hinge;
      calc._cfSnapped = false;
      return calc;
    }

    // A 50mm step on a regular panel keeps the gaps at target; prefer it.
    var guard = 0;
    while (freed >= 50 && guard++ < 200) {
      var placed = false;
      for (var p = 0; p < panels.length; p++) {
        if (onLadder(panels[p] + 50, PANEL_LADDER)) { panels[p] += 50; freed -= 50; placed = true; break; }
      }
      if (!placed) break;
    }

    if (freed > 0) {
      var vi = variableGapIndices(calc, gaps);
      if (vi.length) { var k = 0; while (freed > 0) { gaps[vi[k % vi.length]] += 1; k++; freed--; } }
      else if (hasHinge) { hinge += freed; freed = 0; }
    }

    // Never render a variable gap below the 10mm minimum.
    var vIdx = variableGapIndices(calc, gaps);
    for (var g = 0; g < vIdx.length; g++) {
      var idx = vIdx[g];
      var need = MIN_GAP - gaps[idx];
      for (var q = 0; q < panels.length && need > 0; q++) {
        while (need > 0 && onLadder(panels[q] - 50, PANEL_LADDER)) {
          panels[q] -= 50; gaps[idx] += 50; need -= 50;
        }
      }
    }

    // The section must still add up exactly.
    var drift = L - (sum(panels) + gateW + rakedW + hinge + sum(gaps));
    if (drift !== 0) {
      var v2 = variableGapIndices(calc, gaps), left = Math.abs(drift);
      if (v2.length) {
        var step = drift > 0 ? 1 : -1, c = 0;
        while (left > 0 && c < 100000) {
          var t = v2[c % v2.length];
          if (step < 0 && gaps[t] <= MIN_GAP) { c++; if (c > v2.length * 3) break; continue; }
          gaps[t] += step; left--; c++;
        }
      }
      if (left > 0 && hasHinge) hinge += (drift > 0 ? left : -left);
    }

    calc._cfSnapped = String(calc.panelWidths) !== String(panels) ||
                      String(calc.gapSpacings) !== String(gaps) ||
                      (hasHinge && calc.hingeWidth !== hinge);
    calc.panelWidths = panels;
    calc.gapSpacings = gaps;
    if (hasHinge) calc.hingeWidth = hinge;
    if (panels.length && calc.panelWidth != null) calc.panelWidth = panels[0];
    calc.totalGapWidth = sum(gaps);
    return calc;
  }

  function hingeUnitPrice(self, width, section) {
    var products = self.shopifyData && self.shopifyData.products;
    if (!products) return null;
    var ht = (section && section.hingeType) || 'spring';
    var product = ht === 'soft-close'
      ? (products.hingeSoftClosePanel && products.hingeSoftClosePanel.variants &&
         products.hingeSoftClosePanel.variants.length ? products.hingeSoftClosePanel : products.hingeStandardPanel)
      : products.hingeStandardPanel;
    if (!product || !product.variants || !product.variants.length) return null;

    var v = null;
    for (var i = 0; i < product.variants.length; i++) {
      if (parseInt(product.variants[i].title || product.variants[i].option1, 10) === Number(width)) {
        v = product.variants[i]; break;
      }
    }
    if (!v || !(v.price > 0)) return null;
    var price = v.price / 100;

    // James Glen has no Enduroshield hinge panel; carry across the coating
    // uplift measured on the plain panel at the same width.
    if (section && section.enduroShield && products.enduroshieldPanel && products.poolPanelBase) {
      var base = null, coated = null;
      (products.poolPanelBase.variants || []).forEach(function (x) {
        if (parseInt(x.title || x.option1, 10) === Number(width)) base = x.price;
      });
      (products.enduroshieldPanel.variants || []).forEach(function (x) {
        if (parseInt(x.title || x.option1, 10) === Number(width)) coated = x.price;
      });
      if (base > 0 && coated > 0) price = price * (coated / base);
    }
    return Math.round(price * 100) / 100;
  }

  function applyPatch(proto) {
    if (!proto || proto[FLAG]) return true;
    var origCalc = proto.calculateOptimalPanels,
        origQuote = proto._calcSectionQuote,
        origLayout = proto.generateSectionPanelLayout,
        origCart = proto._buildCartItemsForSection,
        origUpdate = proto.updateCalculationResults,
        origRender = proto.renderSectionPreview;
    if (typeof origCalc !== 'function' || typeof origQuote !== 'function') return false;

    // state.optimizedConfigs[n] is the cached calculation the drawn preview, the
    // quote breakdown and the cart all read. Snap it as soon as it is built —
    // and again at each point it is consumed, because editing a field can queue
    // a later recalculation that replaces the object after this has returned.
    if (typeof origUpdate === 'function') {
      proto.updateCalculationResults = function (sectionNum, prioritizeGap, prioritizePanel) {
        // 6 — in wall-to-glass the gate hinges off the wall, so the wall is
        // always at the gate's own end. Without wallGateEnd the solver and the
        // renderer disagree about whether a hinge panel exists and a whole
        // panel is dropped from both the drawing and the quote.
        try {
          var s0 = this.state && this.state.sections && this.state.sections[sectionNum];
          if (s0 && s0.hasGate && s0.gateHingeType === 'wall-to-glass') {
            s0.wallGateEnd = s0.gateSide === 'left' ? 'left' : 'right';
          } else if (s0 && s0.wallGateEnd) {
            s0.wallGateEnd = null;   // stale value from a previous selection
          }
        } catch (e) { /* fall through to the original behaviour */ }

        var out = origUpdate.call(this, sectionNum, prioritizeGap, prioritizePanel);
        try {
          var cfg = this.state && this.state.optimizedConfigs && this.state.optimizedConfigs[sectionNum];
          var section = this.state && this.state.sections && this.state.sections[sectionNum];
          if (cfg && section) snapCalculation(cfg, section, this);
        } catch (e) { /* never break the calculator over a display correction */ }
        return out;
      };
    }

    // Fallback path, used before a cached config exists.
    proto.calculateOptimalPanels = function (section, prioritizeGap, prioritizePanel) {
      var calc = origCalc.call(this, section, prioritizeGap, prioritizePanel);
      try { return snapCalculation(calc, section, this); } catch (e) { return calc; }
    };

    // Snap immediately before drawing. snapCalculation is idempotent — a config
    // already on the ladder comes back untouched — so re-snapping costs nothing.
    if (typeof origRender === 'function') {
      proto.renderSectionPreview = function (sectionNum) {
        try {
          var cfg = this.state && this.state.optimizedConfigs && this.state.optimizedConfigs[sectionNum];
          var sec = this.state && this.state.sections && this.state.sections[sectionNum];
          if (cfg && sec) snapCalculation(cfg, sec, this);
        } catch (e) { /* draw whatever we have */ }
        return origRender.apply(this, arguments);
      };
    }

    proto._calcSectionQuote = function (sectionNum, section, calculation) {
      try { snapCalculation(calculation, section, this); } catch (e) { /* quote what we have */ }
      var quote = origQuote.call(this, sectionNum, section, calculation);
      try {
        if (!quote || !Array.isArray(quote.breakdown)) return quote;
        var self = this, delta = 0;
        quote.breakdown.forEach(function (line) {
          var m = line && line.item && String(line.item).match(/^(\d+)mm Gate Hinge Panel/);
          if (!m) return;
          var width = Number(m[1]);
          // Only correct a line still carrying the plain-panel price. Sections
          // quoted per lineal metre are left alone.
          var plain = self._glassPanelUnitPrice ? self._glassPanelUnitPrice(width, section) : null;
          if (plain == null || Math.abs(plain - line.unitPrice) > 0.011) return;
          var correct = hingeUnitPrice(self, width, section);
          if (correct == null || correct <= 0) return;
          var qty = line.quantity || 1;
          delta += (correct - line.unitPrice) * qty;
          line.unitPrice = correct;
          line.totalPrice = Math.round(correct * qty * 100) / 100;
        });
        if (delta) quote.subtotal = Math.round((quote.subtotal + delta) * 100) / 100;
      } catch (e) { /* pricing correction is best-effort */ }
      return quote;
    };

    if (typeof origLayout === 'function') {
      proto.generateSectionPanelLayout = function (section, calculation) {
        try { snapCalculation(calculation, section, this); } catch (e) { /* lay out what we have */ }
        var panels = origLayout.call(this, section, calculation);
        try {
          if (Array.isArray(panels) && calculation && calculation.hingeWidth > 0) {
            panels.forEach(function (p) { if (p && p.type === 'hinge') p.length = calculation.hingeWidth; });
          }
        } catch (e) { /* leave the layout as built */ }
        return panels;
      };
    }

    if (typeof origCart === 'function') {
      proto._buildCartItemsForSection = function (section, calculation) {
        try { snapCalculation(calculation, section, this); } catch (e) { /* build from what we have */ }
        var before = (this.state && this.state.cartItems && this.state.cartItems.length) || 0;
        var out = origCart.apply(this, arguments);

        // 7 — a raked panel beside a gate can leave a run that cannot be built.
        // Neither case is repairable by resizing glass, so report it rather than
        // quote a fence we cannot supply.
        try {
          var c = calculation, msgs = [];
          if (c && c.hingePanels && c.hingeWidth > 0 && c.hingeWidth < HINGE_LADDER[0]) {
            msgs.push('This section is too short to fit a gate beside a raked panel — the ' +
                      'smallest hinge panel made is ' + HINGE_LADDER[0] + 'mm. Email us and ' +
                      'we will size it for you.');
          }
          if (c && Array.isArray(c.gapSpacings)) {
            var vv = variableGapIndices(c, c.gapSpacings), worst = 0;
            for (var z = 0; z < vv.length; z++) {
              if (c.gapSpacings[vv[z]] > worst) worst = c.gapSpacings[vv[z]];
            }
            if (worst > MAX_GAP) {
              msgs.push('This section leaves a ' + worst + 'mm gap, wider than the ' + MAX_GAP +
                        'mm we build to, and the drawing can spread it past the 100mm ' +
                        'pool-safety limit. Email us and we will size it for you.');
            }
          }
          for (var mi = 0; mi < msgs.length; mi++) {
            if (this.state.unresolvedComponents &&
                this.state.unresolvedComponents.indexOf(msgs[mi]) === -1) {
              this.state.unresolvedComponents.push(msgs[mi]);
            }
          }
        } catch (e) { /* advisory only */ }

        try {
          var items = this.state && this.state.cartItems;
          if (!items) return out;
          // 8 — wall-to-glass has no hinge panel: the gate hinges off the wall.
          // The cart builder added one anyway, sized from the last regular panel
          // (a 2000mm run ordered an 1150mm "hinge panel"), putting a part on the
          // order that is not part of the fence.
          var noHinge = !calculation || !calculation.hingePanels;
          for (var i = items.length - 1; i >= before; i--) {
            var m2 = items[i] && items[i].title && String(items[i].title).match(/^(\d+)mm Gate Hinge Panel/);
            if (!m2) continue;
            if (noHinge) { items.splice(i, 1); continue; }   // not needed, not an error
            var width = Number(m2[1]);
            if (onLadder(width, HINGE_LADDER)) continue;
            items.splice(i, 1);
            if (this.state.unresolvedComponents &&
                this.state.unresolvedComponents.indexOf(width + 'mm Gate Hinge Panel') === -1) {
              this.state.unresolvedComponents.push(width + 'mm Gate Hinge Panel');
            }
          }
        } catch (e) { /* leave the cart as built */ }
        return out;
      };
    }

    proto[FLAG] = true;
    try { console.log('[ClickFencing] catalogue size + hinge pricing fix applied'); } catch (e) {}
    return true;
  }

  function tryInstall() {
    var inst = window.glassFencingCalculator || window.glassCalculator;
    if (inst && applyPatch(Object.getPrototypeOf(inst))) return;
    if (++tries < MAX_TRIES) setTimeout(tryInstall, 100);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', tryInstall);
  tryInstall();
})();
